/**
 * Codigo de seguranca de 6 palavras — a defesa contra man-in-the-middle.
 *
 * Ate os dois lados compararem estas palavras por um canal fora de banda
 * (voz, presencialmente), nada impede um servidor malicioso de ter entregue
 * um bundle falso. Por isso a UI marca o contato como NAO_VERIFICADO ate a
 * confirmacao manual.
 *
 * Entropia: 6 x log2(2048) = 66 bits. Para forjar um par de chaves que
 * produza o mesmo codigo, o atacante precisaria de ~2^66 tentativas.
 */
import { compareBytes, concat } from './bytes.js'
import { DOMAIN } from './kdf.js'
import { sodium } from './sodium.js'
import { WORDLIST_PT_BR } from './wordlist-pt-br.js'

export const SAFETY_CODE_WORDS = 6
export const SAFETY_CODE_BITS_PER_WORD = 11
export const WORDLIST_SIZE = 2048

export interface SafetyCode {
  readonly words: readonly string[]
  readonly text: string
}

/**
 * Os fingerprints sao ordenados antes do hash. Sem isso, Alice e Bob
 * calculariam codigos diferentes e a verificacao seria inutil.
 */
export function deriveSafetyCode(fingerprintA: Uint8Array, fingerprintB: Uint8Array): SafetyCode {
  const [low, high] =
    compareBytes(fingerprintA, fingerprintB) <= 0
      ? [fingerprintA, fingerprintB]
      : [fingerprintB, fingerprintA]

  const digest = sodium().crypto_generichash(32, concat(DOMAIN.safetyCode, low, high))

  const words: string[] = []
  let bitPos = 0
  for (let i = 0; i < SAFETY_CODE_WORDS; i++) {
    let index = 0
    for (let bit = 0; bit < SAFETY_CODE_BITS_PER_WORD; bit++) {
      const byte = digest[bitPos >> 3] ?? 0
      const value = (byte >> (7 - (bitPos & 7))) & 1
      index = (index << 1) | value
      bitPos += 1
    }
    words.push(WORDLIST_PT_BR[index] ?? '')
  }
  return { words, text: words.join(' ') }
}
